import { useEffect, useRef, useState } from "react";
import { ConfigGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/ConfigGraphQlAdapter";
import {
  configListCacheKey,
  getConfigListCacheEntry,
  setConfigListCacheEntry,
  type ConfigListCacheScope,
} from "./configListCache";
import {
  useConfigListPagination,
  type ConfigPageResult,
} from "./useConfigListPagination";

export interface ConfigListFetchArgs {
  search: string | null;
  page: number;
  limit: number;
}

export interface ConfigListFetchResult<TItem> extends ConfigPageResult {
  items: TItem[];
}

interface UseConfigListQueryOptions<TItem> {
  brandKey: string;
  defaultPageSize: number;
  errorMessage: string;
  cacheScope: ConfigListCacheScope;
  fetchPage: (
    args: ConfigListFetchArgs,
  ) => Promise<ConfigListFetchResult<TItem>>;
}

/**
 * Shared Config list load cycle with page cache:
 * set X-Brand-Key → cache hit or paginated fetch → items + pagination state.
 * Cache survives submenu remounts; cleared when leaving Config layout.
 * `refetch()` forces a network reload for the current page key.
 */
export function useConfigListQuery<TItem>({
  brandKey,
  defaultPageSize,
  errorMessage,
  cacheScope,
  fetchPage,
}: UseConfigListQueryOptions<TItem>) {
  const initialCacheKey = configListCacheKey(
    cacheScope,
    brandKey,
    1,
    defaultPageSize,
    "",
  );
  const initialCached = getConfigListCacheEntry<TItem>(initialCacheKey);

  const {
    loading,
    setLoading,
    error,
    setError,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    reloadToken,
    refetch,
    applyPageResult,
    applyLoadError,
  } = useConfigListPagination(brandKey, defaultPageSize, {
    initialLoading: !initialCached,
    initialTotal: initialCached?.total ?? 0,
  });

  const [items, setItems] = useState<TItem[]>(
    () => initialCached?.items ?? [],
  );
  const lastReloadTokenRef = useRef(0);

  useEffect(() => {
    ConfigGraphQlAdapter.getInstance().setBrandKey(brandKey);
    return () => {
      ConfigGraphQlAdapter.getInstance().setBrandKey(null);
    };
  }, [brandKey]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = configListCacheKey(
      cacheScope,
      brandKey,
      page,
      limit,
      search,
    );
    const forceRefresh = reloadToken !== lastReloadTokenRef.current;
    const cached = getConfigListCacheEntry<TItem>(cacheKey);

    if (cached && !forceRefresh) {
      setItems(cached.items);
      applyPageResult(cached);
      setLoading(false);
      setError(null);
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPage({
          search: search.trim() || null,
          page,
          limit,
        });
        if (cancelled) return;
        setConfigListCacheEntry(cacheKey, result);
        lastReloadTokenRef.current = reloadToken;
        setItems(result.items);
        applyPageResult(result);
      } catch (err) {
        if (cancelled) return;
        setItems([]);
        applyLoadError(err, errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    brandKey,
    cacheScope,
    fetchPage,
    reloadToken,
    page,
    limit,
    search,
    errorMessage,
    setLoading,
    setError,
    applyPageResult,
    applyLoadError,
  ]);

  return {
    items,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  };
}
